import { Truck, Package, ShieldCheck, Users } from 'lucide-react';
import { serviceSpecIcons } from './serviceSpecIcons';

const SERVICE_IMAGES = {
  banner: { src: '/images/paper-manufacturing.jpg', alt: 'Greeneri paper manufacturing facility' },
  accent: { src: '/images/paper-products.jpg', alt: 'Greeneri paper products' },
};

export const servicesRegistry = {
  bulkSupply: {
    slug: 'bulk-supply',
    navId: 'bulk',
    num: '01',
    hue: 'green',
    icon: Truck,
    coverImage: {
      src: '/images/paper-manufacturing.jpg',
      alt: 'Greeneri bulk paper supply from Meerut',
    },
    kicker: 'Volume orders',
    title: 'Bulk Supply',
    headline: 'Repeat bulk reels & sheets for plants across India',
    summary:
      'Scheduled bulk supply of PE coated rolls, food wrap, cup stock and specialty grades — built for converters, distributors and FMCG lines that need dependable repeat volumes.',
    highlights: [
      'MOQ-friendly production runs from Meerut',
      'Pan-India repeat dispatch schedules',
      'PE coated, food-grade & specialty paper grades',
      'Consistent GSM and reel dimensions batch to batch',
    ],
    body: [
      'Greeneri supports high-volume B2B buyers with reel and sheet supply aligned to your converting line. Whether you run cup forming, food wrap, or packaging converting, we plan bulk dispatches so your floor never waits on material.',
      'Our Meerut facility handles repeat orders with batch-wise control — from fibre intake through coating, QC and sealed dispatch. You get the same specs every run, on timelines you can build production around.',
    ],
    hero: {
      badge: 'Volume orders',
      scriptTitle: 'B2B Paper Supply',
      boldTitle: 'Bulk Supply',
      descriptions: [
        'Scheduled bulk supply of PE coated rolls, food wrap, cup stock and specialty grades — built for converters, distributors and FMCG lines that need dependable repeat volumes.',
        'Greeneri supports high-volume B2B buyers with reel and sheet supply aligned to your converting line. Our Meerut facility handles repeat orders with batch-wise control from fibre intake to sealed dispatch.',
      ],
      features: [
        'MOQ-friendly production runs',
        'Pan-India repeat schedules',
        'PE coated & food-grade grades',
        'Consistent GSM every batch',
      ],
      bannerImage: SERVICE_IMAGES.banner,
      accentImage: SERVICE_IMAGES.accent,
    },
    specs: {
      supply: {
        label: 'Bulk supply',
        value: 'Reel & jumbo roll orders for converters and distributors',
        icon: serviceSpecIcons.supply,
      },
      schedule: {
        label: 'Repeat schedules',
        value: 'Monthly and quarterly dispatch plans you can rely on',
        icon: serviceSpecIcons.schedule,
      },
      grades: {
        label: 'Paper grades',
        value: 'PE coated, food-grade and specialty virgin base stock',
        icon: serviceSpecIcons.quality,
      },
      dispatch: {
        label: 'Pan-India dispatch',
        value: 'Coordinated logistics from Meerut across India',
        icon: serviceSpecIcons.dispatch,
      },
    },
    benefits: [
      {
        title: 'Volume Planning',
        description:
          'Repeat bulk orders aligned to your plant consumption — so material arrives when your line needs it, not when it is convenient.',
      },
      {
        title: 'Grade Consistency',
        description:
          'Same GSM, width and coating spec batch after batch. Build production schedules without re-qualifying every delivery.',
      },
      {
        title: 'Pan-India Reach',
        description:
          'Dispatch coordination nationwide for distributors, converters and FMCG supply chains from our Meerut facility.',
      },
      {
        title: 'B2B Partnership',
        description:
          'Dedicated sales support for specs, timelines and repeat orders — one team from quote to dispatch.',
      },
    ],
    benefitsTitle: 'Bulk Supply Benefits',
    benefitsSubtitle:
      'Why converters and distributors choose Greeneri for high-volume paper supply across India.',
    ctaTitle: 'Plan your next bulk order',
    ctaSubtitle:
      'Share GSM, quantity and dispatch timeline — our Meerut team will align production and logistics to your schedule.',
  },
  customPackaging: {
    slug: 'custom-packaging',
    navId: 'custom',
    num: '02',
    hue: 'coral',
    icon: Package,
    coverImage: {
      src: '/images/paper-products.jpg',
      alt: 'Custom packaging paper grades from Greeneri',
    },
    kicker: 'Your specs',
    title: 'Custom Packaging',
    headline: 'Paper grades tailored to your product & line',
    summary:
      'Custom GSM, width, coating and finish for food wrap, cup sleeves, packaging and specialty applications — manufactured to your brand and converting requirements.',
    highlights: [
      'Custom GSM, width & reel size',
      'Food-grade PE and specialty coatings',
      'Specs aligned to your converting equipment',
      'Brand-ready material for packaging lines',
    ],
    body: [
      'Not every line runs the same spec. We work with your technical team to match coating type, GSM range, reel width and surface finish to how your machines run — reducing waste and changeover on the floor.',
      'From food-contact wraps to cup stock and rigid packaging grades, custom runs are produced under the same batch-wise QC as our standard catalogue — so custom does not mean inconsistent.',
    ],
    hero: {
      badge: 'Your specs',
      scriptTitle: 'Tailored Paper Grades',
      boldTitle: 'Custom Packaging',
      descriptions: [
        'Custom GSM, width, coating and finish for food wrap, cup sleeves, packaging and specialty applications — manufactured to your brand and converting requirements.',
        'We match coating type, reel width and surface finish to how your machines run. Custom runs follow the same batch-wise QC as our standard catalogue.',
      ],
      features: [
        'Custom GSM & width',
        'Food-grade coatings',
        'Line-matched specs',
        'Sample reels available',
      ],
      bannerImage: SERVICE_IMAGES.banner,
      accentImage: SERVICE_IMAGES.accent,
    },
    specs: {
      development: {
        label: 'Spec development',
        value: 'Custom GSM, width and reel dimensions for your line',
        icon: serviceSpecIcons.custom,
      },
      coating: {
        label: 'Coating options',
        value: 'Food-grade PE and specialty coatings for your application',
        icon: serviceSpecIcons.quality,
      },
      samples: {
        label: 'Sample reels',
        value: 'Trial material before bulk confirmation and production sign-off',
        icon: serviceSpecIcons.sample,
      },
      documentation: {
        label: 'QA documentation',
        value: 'Spec sheets and batch details for your quality team',
        icon: serviceSpecIcons.report,
      },
    },
    benefits: [
      {
        title: 'Line-Matched Specs',
        description:
          'GSM, width and coating tuned to your converting equipment — less waste, fewer changeovers, smoother runs.',
      },
      {
        title: 'Food-Grade Options',
        description:
          'PE and specialty coatings for food-contact wraps, cup stock and packaging grades that meet your compliance needs.',
      },
      {
        title: 'Sample Before Bulk',
        description:
          'Trial reels for machine testing and approval before committing to full production volumes.',
      },
      {
        title: 'Consistent Custom Runs',
        description:
          'Custom does not mean inconsistent — every batch follows the same QC process as our catalogue grades.',
      },
    ],
    benefitsTitle: 'Custom Packaging Benefits',
    benefitsSubtitle:
      'Paper grades built around your product, your machines and your brand requirements.',
    ctaTitle: 'Discuss your custom spec',
    ctaSubtitle:
      'Tell us your GSM, width, coating and volume — we will develop a grade matched to your converting line.',
  },
  qualityTesting: {
    slug: 'quality-testing',
    navId: 'quality',
    num: '03',
    hue: 'mint',
    icon: ShieldCheck,
    coverImage: {
      src: '/images/paper-manufacturing.jpg',
      alt: 'Quality testing on Greeneri paper batches',
    },
    kicker: 'Batch-wise QC',
    title: 'Quality Testing',
    headline: 'Every batch checked before it leaves Meerut',
    summary:
      'GSM, surface finish and food-contact grades verified batch-wise on the factory floor — so what reaches your line matches what you approved.',
    highlights: [
      'GSM & thickness consistency checks',
      'Surface finish & coating adhesion tests',
      'Food-contact grade verification',
      'Batch documentation for traceability',
    ],
    body: [
      'Quality is not a final gate — it runs through every stage at Greeneri. Raw fibre and pulp are screened before reel intake; coating lines are monitored for even application; finished reels are checked for GSM, finish and food-contact compliance before wrapping.',
      'For B2B buyers, this means fewer surprises at converting. You receive material that performs the same way run after run, with specs you can reference when planning orders.',
    ],
    hero: {
      badge: 'Batch-wise QC',
      scriptTitle: 'Factory Floor Quality',
      boldTitle: 'Quality Testing',
      descriptions: [
        'GSM, surface finish and food-contact grades verified batch-wise on the factory floor — so what reaches your line matches what you approved.',
        'From raw fibre screening to coating monitoring and pre-dispatch checks — quality runs through every stage at our Meerut facility.',
      ],
      features: [
        'GSM consistency checks',
        'Surface finish tests',
        'Food-grade verification',
        'Batch traceability',
      ],
      bannerImage: SERVICE_IMAGES.banner,
      accentImage: SERVICE_IMAGES.accent,
    },
    specs: {
      gsm: {
        label: 'GSM & thickness',
        value: 'Batch-wise consistency checks on every production run',
        icon: serviceSpecIcons.report,
      },
      finish: {
        label: 'Surface finish',
        value: 'Coating adhesion and surface quality verified before dispatch',
        icon: serviceSpecIcons.quality,
      },
      compliance: {
        label: 'Food-grade compliance',
        value: 'Food-contact grades checked against your application requirements',
        icon: serviceSpecIcons.quality,
      },
      signoff: {
        label: 'Pre-dispatch sign-off',
        value: 'Every reel cleared before wrapping and labelled dispatch',
        icon: serviceSpecIcons.sample,
      },
    },
    benefits: [
      {
        title: 'Batch-Wise Control',
        description:
          'Every production run checked on the factory floor — not just a final inspection at the gate.',
      },
      {
        title: 'Fewer Converting Surprises',
        description:
          'Material that performs the same way run after run, reducing downtime and rejected reels on your line.',
      },
      {
        title: 'Food-Contact Assurance',
        description:
          'Food-grade compliance verified for wraps, cup stock and packaging grades before dispatch.',
      },
      {
        title: 'Traceable Specs',
        description:
          'Batch documentation you can reference when planning repeat orders and auditing your supply chain.',
      },
    ],
    benefitsTitle: 'Quality Testing Benefits',
    benefitsSubtitle:
      'Rigorous batch-wise QC that protects your converting line and your brand reputation.',
    ctaTitle: 'Ask about our QC process',
    ctaSubtitle:
      'Want to understand how we test GSM, coating and food-grade compliance? Talk to our Meerut quality team.',
  },
  logisticsSupport: {
    slug: 'logistics-support',
    navId: 'logistics',
    num: '04',
    hue: 'sun',
    icon: Users,
    coverImage: {
      src: '/images/operator.png',
      alt: 'Greeneri logistics and B2B support team',
    },
    kicker: 'End-to-end',
    title: 'Logistics Support',
    headline: 'Dispatch planning & supply coordination nationwide',
    summary:
      'Dedicated B2B support for order tracking, bulk schedules and repeat supply — so your procurement team has one point of contact from quote to delivery.',
    highlights: [
      'Pan-India dispatch coordination',
      'Repeat bulk schedule planning',
      'Order status & timeline updates',
      'Long-term supply partnership support',
    ],
    body: [
      'Paper supply is not just manufacturing — it is timing. Our logistics support helps you plan repeat bulk orders, align dispatch with your plant consumption, and keep communication clear from Meerut to your warehouse.',
      'Whether you are a distributor stocking multiple grades or a plant running a single spec on a fixed schedule, we coordinate dispatches across India with the same attention we give to production quality.',
    ],
    hero: {
      badge: 'End-to-end',
      scriptTitle: 'Supply Coordination',
      boldTitle: 'Logistics Support',
      descriptions: [
        'Dedicated B2B support for order tracking, bulk schedules and repeat supply — one point of contact from quote to delivery.',
        'We help you plan repeat bulk orders, align dispatch with plant consumption, and coordinate Pan-India logistics from Meerut.',
      ],
      features: [
        'Pan-India dispatch',
        'Repeat schedule planning',
        'Order status updates',
        'Dedicated B2B contact',
      ],
      bannerImage: {
        src: '/images/operator.png',
        alt: 'Greeneri logistics and B2B support team',
      },
      accentImage: SERVICE_IMAGES.accent,
    },
    specs: {
      timeline: {
        label: 'Dispatch timelines',
        value: 'Coordinated delivery schedules aligned to your consumption',
        icon: serviceSpecIcons.schedule,
      },
      repeat: {
        label: 'Repeat orders',
        value: 'Monthly and quarterly bulk schedules you can plan production on',
        icon: serviceSpecIcons.supply,
      },
      shipment: {
        label: 'Bulk shipments',
        value: 'Multi-reel and multi-grade dispatch planning across India',
        icon: serviceSpecIcons.dispatch,
      },
      contact: {
        label: 'Dedicated support',
        value: 'Single B2B contact from quote through delivery and reorder',
        icon: serviceSpecIcons.support,
      },
    },
    benefits: [
      {
        title: 'Clear Communication',
        description:
          'One dedicated contact for order status, timeline updates and dispatch coordination — no chasing multiple departments.',
      },
      {
        title: 'Schedule Alignment',
        description:
          'Repeat bulk orders planned around your plant consumption so material arrives when you need it.',
      },
      {
        title: 'Pan-India Coverage',
        description:
          'Dispatch coordination nationwide for distributors, converters and multi-location supply chains.',
      },
      {
        title: 'Long-Term Partnership',
        description:
          'We support repeat supply relationships — not one-off transactions — with schedules you can build on.',
      },
    ],
    benefitsTitle: 'Logistics Support Benefits',
    benefitsSubtitle:
      'End-to-end supply coordination so your procurement team stays in control from Meerut to your warehouse.',
    ctaTitle: 'Plan your dispatch schedule',
    ctaSubtitle:
      'Tell us your delivery locations, volumes and frequency — we will coordinate logistics from our Meerut facility.',
  },
};

export const serviceList = Object.values(servicesRegistry);

export const getServiceBySlug = (slug) =>
  serviceList.find((service) => service.slug === slug);

export const getServicePageConfig = (key) => servicesRegistry[key];
